import { formatarDinheiro } from '../../helper';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ModalGeneric from '../../Components/generic/ModalGeneric';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import LinearProgress from '@mui/joy/LinearProgress';
import PedidoLV from '../../Entity/PedidoLV';
import PedidoProdutoLV from '../../Entity/PedidoProdutoLV';
import apiPedidoProdutoLV from '../../api/apiPedidoProdutoLV';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import EdTabela from '../../Generic/EdTabela/EdTabela';
import Coluna from '../../Generic/EdTabela/Coluna';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import EdModal from '../../Components/generic/EdModal';

type Props = {
  pedidoSelecionado: PedidoLV;
  onClose: () => void;
  open: boolean;
};

const ModalDetalhesPedidoEmAnalise: React.FC<Props> = ({ pedidoSelecionado, onClose, open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sessao] = useSessao();
  const corPrincipal = sessao.empresa?.corPrincipal ?? '#1976d2';

  const [loading, setLoading] = useState(true);
  const [produtos, setProdutos] = useState<PedidoProdutoLV[]>([]);

  const cabecalhoTabela = [
    new Coluna<PedidoProdutoLV>({
      label: 'Produto',
      render: (item) => item.produto.descricao,
      align: 'left'
    }),
    new Coluna<PedidoProdutoLV>({
      label: 'Qtd.',
      render: (item) => item.quantidade,
      align: 'center'
    }),
    new Coluna<PedidoProdutoLV>({
      label: '(R$) Unitário',
      render: (item) => `${formatarDinheiro(item.valor_unitario)}`,
      align: 'right'
    }),
    new Coluna<PedidoProdutoLV>({
      label: '(R$) Total',
      render: (item) => <strong>{formatarDinheiro(item.total)}</strong>,
      align: 'right'
    })
  ];

  const getProdutos = async () => {
    setLoading(true);
    try {
      const res = await apiPedidoProdutoLV.getMany({
        where: { id_pedido: pedidoSelecionado.numeroPedidoLV },
        relations: ['produto']
      });
      setProdutos(res);
    } catch (err: any) {
      toast.error('Erro ao buscar itens do pedido.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) getProdutos();
  }, [open, pedidoSelecionado.numeroPedidoLV]);

  return (
    <EdModal open={open} onClose={onClose}>
      <Box sx={{ p: isMobile ? 0 : 1 }}>
        <Box mb={3}>
          <Typography variant="caption" color="textSecondary">
            PEDIDO EM ANÁLISE
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            #{pedidoSelecionado.numeroPedidoLV}
          </Typography>
        </Box>

        {loading ? (
          <Box py={4} textAlign="center">
            <LinearProgress color="primary" sx={{ height: 2, borderRadius: 1 }} />
            <Typography variant="caption" color="textSecondary" mt={1} display="block">
              Carregando itens...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderLeft: '6px solid',
                    borderLeftColor: corPrincipal,
                    bgcolor: '#f8fafc'
                  }}
                >
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    VALOR TOTAL
                  </Typography>
                  <Typography variant="h4" fontWeight="900" color={corPrincipal}>
                    R$ {formatarDinheiro(pedidoSelecionado.valorPedido)}
                  </Typography>
                  <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Status: <strong>Em Análise</strong>
                  </Typography>
                </Paper>

                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    Este pedido aguarda conferência para ser processado.
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                mb={1.5}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ShoppingBagIcon fontSize="small" /> ITENS DO PEDIDO ({produtos.length})
              </Typography>

              {!isMobile ? (
                <EdTabela
                  chave={'TabelaEdClienteDetalhesEmAnalise'}
                  cabecalhoTabela={cabecalhoTabela}
                  linhas={produtos}
                  compactar
                  esconderToolbar
                  elevation={0}
                  maxHeight={350}
                  loading={false}
                />
              ) : (
                <Stack spacing={1.5}>
                  {produtos.map((item) => (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {item.produto.descricao}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1.5}
                      >
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Qtd x Unitário
                          </Typography>
                          <Typography variant="body2">
                            {item.quantidade} un x R$ {formatarDinheiro(item.valor_unitario)}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" color="textSecondary" display="block">
                            Subtotal
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            R$ {formatarDinheiro(item.total)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </EdModal>
  );
};

export default ModalDetalhesPedidoEmAnalise;
